import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { type UploadProcessingStage, type ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";

// Deliberately NOT a continuously-live card mirroring the sidebar row — this
// is a distinct, punchier notification style: one short "beat" at a time,
// each visible for CARD_VISIBLE_MS then gone, checking in again every
// CARD_CYCLE_MS. If the real stage hasn't moved since the last beat, the
// next beat says "Still X" (still in progress, red). If it has moved, the
// OLD stage's "done" beat (green) plays first, immediately followed by the
// new stage's beat — repeat until a terminal status ends the sequence.
const CARD_VISIBLE_MS = 2000;
const CARD_CYCLE_MS = 5000;
// How long the slide/fade itself takes, each way — deliberately slow and
// unhurried (a snappy 300ms read as too abrupt for "appears, sits, fades").
// EXIT_GAP_MS must stay >= this: it's the pause between one card fully
// finishing its own exit and the next one starting to enter, so a slower
// transition here always means a proportionally longer gap too — the
// outgoing card must be completely gone before the next one begins, or
// they'd visibly overlap mid-slide.
const TRANSITION_MS = 700;
const EXIT_GAP_MS = TRANSITION_MS + 50;
// However many uploads happen at once, the stack never shows more than this
// many cards — the rest still track normally in "Your Uploads," they just
// don't each get their own beat sequence cluttering the corner.
const MAX_VISIBLE_TOASTS = 4;

type BeatTone = "progress" | "success" | "error" | "neutral";

const TONE_BG: Record<BeatTone, string> = {
  progress: "bg-[#FFF5F7]",
  success: "bg-[#F0FDF4]",
  error: "bg-[#FEF2F2]",
  neutral: "bg-[#F3F4F6]",
};
const TONE_TEXT: Record<BeatTone, string> = {
  progress: "text-[#DC2626]",
  success: "text-[#16A34A]",
  error: "text-[#DC2626]",
  neutral: "text-[#6B7280]",
};

/**
 * Three phrasings per stage — "progress" (just started/current), "still" (a
 * heartbeat check-in found nothing has changed — always red, a genuine
 * ongoing wait), "done" (this stage just finished, shown once, in green,
 * right before the next stage's own "progress" beat). Reused for a lone
 * PDF, a lone structured export, or a mixed zip alike — extracting/
 * converting only ever fire when the upload actually contains a PDF (see
 * get_upload_processing_progress), so this copy is already correct either
 * way without knowing the file type.
 *
 * progressTone overrides the default red for a stage's "progress" phrasing
 * only — "queued"'s is "Received!", which describes something that already
 * happened (the file made it to the server), not an action still underway,
 * so it reads as a green confirmation rather than a red in-progress state.
 * Its OWN "still" phrasing ("Still getting ready…") stays red, since that
 * one only ever shows if this stage genuinely lingers — a real wait.
 */
const STAGE_BEATS: Record<UploadProcessingStage, { progress: string; progressTone?: BeatTone; still: string; done: string }> = {
  uploading: { progress: "Uploading", still: "Still uploading…", done: "Uploaded!" },
  queued: { progress: "Received!", progressTone: "success", still: "Still getting ready…", done: "Ready to process!" },
  extracting: { progress: "Reading your document", still: "Still reading your document…", done: "Document read!" },
  converting: { progress: "Analyzing with AI", still: "Still analyzing with AI…", done: "Analysis complete!" },
  processing: { progress: "Processing", still: "Still processing…", done: "Processing complete!" },
};

const TERMINAL_BEATS: Record<"complete" | "failed" | "cancelled", { title: string; tone: BeatTone }> = {
  complete: { title: "All done!", tone: "success" },
  failed: { title: "Something went wrong", tone: "error" },
  cancelled: { title: "Cancelled", tone: "neutral" },
};

type BeatKey = UploadProcessingStage | "complete" | "failed" | "cancelled";

function isTerminalKey(key: BeatKey): key is "complete" | "failed" | "cancelled" {
  return key === "complete" || key === "failed" || key === "cancelled";
}

function keyFor(entry: ZipUploadEntry): BeatKey {
  if (entry.status !== "processing") return entry.status;
  return entry.processingStage ?? "processing";
}

function beatForKey(k: BeatKey, form: "progress" | "still"): { title: string; tone: BeatTone } {
  if (isTerminalKey(k)) {
    const t = TERMINAL_BEATS[k];
    return { title: t.title, tone: t.tone };
  }
  const s = STAGE_BEATS[k];
  const tone: BeatTone = form === "progress" ? (s.progressTone ?? "progress") : "progress";
  return { title: s[form], tone };
}

interface Beat {
  title: string;
  tone: BeatTone;
}

function UploadToastCard({ entry, onDone }: { entry: ZipUploadEntry; onDone: () => void }) {
  const [beat, setBeat] = useState<Beat | null>(null);
  const [visible, setVisible] = useState(false);

  // The prop (entry) can change stage several times faster than a single
  // beat's own display cycle (CARD_VISIBLE_MS + EXIT_GAP_MS) — a real
  // upload's S3 transfer routinely finishes in well under a second, which
  // used to change entry.processingStage from "uploading" to "queued"
  // WHILE the "Uploading" beat's own effect was still mid-animation. That
  // effect was keyed on [entry.status, entry.processingStage], so React
  // re-ran it immediately, tearing down and cancelling the "Uploading"
  // card before requestAnimationFrame had even flipped it visible — it was
  // set as `beat` but never actually shown, and the very next thing the
  // patient saw was "Uploaded!" (done) followed immediately by the next
  // stage's card. This is exactly the bug: a beat could be silently
  // skipped whenever the backend raced ahead of the animation.
  //
  // Fixed by decoupling "the data changed" from "the animation reacts":
  // this ref always holds the LATEST known key, updated by a separate,
  // cheap effect below with no timers of its own. The driver effect (the
  // big one, mount-once) only ever reads this ref at its own natural
  // checkpoints — after a beat has already played its full, uninterrupted
  // cycle — never mid-cycle. So the very first beat (and every one after
  // it) is now guaranteed its complete on-screen time; a fast backend can
  // still race ahead multiple stages, but the driver just picks up
  // whatever the latest key is at its next checkpoint instead of racing
  // the animation and losing.
  const pendingKeyRef = useRef<BeatKey>(keyFor(entry));
  useEffect(() => {
    pendingKeyRef.current = keyFor(entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.status, entry.processingStage]);

  // onDone (dismissToast) is stable from context, but captured via a ref
  // too so the mount-once driver effect below never needs it in a
  // dependency array.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // True once the patient hits the close button — checked before every
  // state update the driver would otherwise make, so a manual dismiss
  // can't be raced by an in-flight beat transition re-appearing for a
  // frame right as the card is unmounting.
  const dismissedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const after = (ms: number, fn: () => void) => {
      const id = window.setTimeout(() => {
        if (!cancelled && !dismissedRef.current) fn();
      }, ms);
      timers.push(id);
    };

    const playCard = (content: Beat, onHidden?: () => void) => {
      if (cancelled || dismissedRef.current) return;
      setBeat(content);
      requestAnimationFrame(() => {
        if (!cancelled && !dismissedRef.current) setVisible(true);
      });
      after(CARD_VISIBLE_MS, () => {
        setVisible(false);
        after(EXIT_GAP_MS, () => onHidden?.());
      });
    };

    const lastKeyRef = { current: null as BeatKey | null };

    const announce = (key: BeatKey) => {
      if (isTerminalKey(key)) {
        playCard(beatForKey(key, "progress"), () => onDoneRef.current());
      } else {
        playCard(beatForKey(key, "progress"), () => after(CARD_CYCLE_MS - CARD_VISIBLE_MS, tick));
      }
    };

    const tick = () => {
      const key = pendingKeyRef.current;
      if (lastKeyRef.current === null) {
        lastKeyRef.current = key;
        announce(key);
        return;
      }
      if (key !== lastKeyRef.current) {
        const oldKey = lastKeyRef.current;
        lastKeyRef.current = key;
        if (!isTerminalKey(oldKey)) {
          const oldStage = STAGE_BEATS[oldKey];
          playCard({ title: oldStage.done, tone: "success" }, () => announce(key));
        } else {
          announce(key);
        }
        return;
      }
      // No change since the last checkpoint — heartbeat. Never for a
      // terminal state (nothing left to check in on).
      if (isTerminalKey(key)) return;
      playCard(beatForKey(key, "still"), () => after(CARD_CYCLE_MS - CARD_VISIBLE_MS, tick));
    };

    tick();

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
    // Mount-once: this effect drives its own animation loop entirely off
    // pendingKeyRef, checked only at its own checkpoints — it must NOT
    // re-run on every prop change, or the exact bug described above comes
    // right back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    dismissedRef.current = true;
    setVisible(false);
    window.setTimeout(() => onDoneRef.current(), TRANSITION_MS);
  };

  if (!beat) return null;

  return (
    <div
      className={cn(
        "pointer-events-auto relative w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-lg transition-all duration-[700ms] ease-in-out",
        visible ? "translate-x-0 opacity-100" : "translate-x-[120%] opacity-0",
      )}
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleClose}
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-[#F3F4F6] hover:text-[#374151]"
      >
        <Icon icon="lucide:x" className="h-3.5 w-3.5" />
      </button>
      <div className="p-4 pr-9">
        <p className={cn("truncate text-sm font-extrabold", TONE_TEXT[beat.tone])}>{beat.title}</p>
        <p className="mt-0.5 truncate text-xs font-semibold text-[#9CA3AF]">{entry.fileName}</p>
      </div>
    </div>
  );
}

/** Bottom-right, Discord/Slack-style stack of short-lived "beat" cards — one
 *  per upload the patient has submitted this session, pulsing in every few
 *  seconds to announce the current pipeline stage (S3 upload → received →
 *  reading the document, if a PDF is involved → AI analysis → done) rather
 *  than one card that sits there continuously mirroring the "Your Uploads"
 *  sidebar row. Mount once, high in the patient layout, so it's visible
 *  regardless of which page the patient is on — see PatientLayout.tsx. */
export function UploadToastStack() {
  const { zipUploads, toastUploadIds, dismissToast } = useUploadHistory();

  const entries = toastUploadIds
    .map((id) => zipUploads.find((z) => z.id === id))
    .filter((entry): entry is ZipUploadEntry => Boolean(entry))
    .slice(-MAX_VISIBLE_TOASTS);

  if (entries.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-3">
      {entries.map((entry) => (
        <UploadToastCard key={entry.id} entry={entry} onDone={() => dismissToast(entry.id)} />
      ))}
    </div>
  );
}
